import { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from "react-native";
import { getPlans } from "../../services/stripeService";
import { FL, FONT, label, body, nums } from "../floodlight";
import MemberCard from "./MemberCard";

// Floodlight (v1.71): a clear column inside the Community glass panel behind a
// hairline; rows brighten under the pointer, avatars are round glass, status
// is a small dot (green = online).

const SIDEBAR_W = 232;

// ─── Status ───────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  online:  FL.pos,
  idle:    FL.amber,
  dnd:     FL.red,
  offline: FL.muted,
};

// ─── Plan grouping helpers ────────────────────────────────────────────────────

const FALLBACK_PLAN_ORDER = ["ultimate", "pro", "basic", "free"];
const PLAN_TIER_COLORS    = ["#F0B232", "#A259FF", "#60a5fa", "#80848E"];

function planTierColor(plan, planOrder) {
  const idx = planOrder.indexOf(plan);
  if (idx === -1) return PLAN_TIER_COLORS[PLAN_TIER_COLORS.length - 1];
  return PLAN_TIER_COLORS[Math.min(idx, PLAN_TIER_COLORS.length - 1)];
}

function planKey(member) {
  // Member cards carry the server-computed effective plan (communityJoin).
  return String(member.planType || "FREE").toLowerCase();
}

function planLabel(plan, count, plansMap) {
  const name = plansMap[plan]?.name?.toUpperCase() ?? plan.toUpperCase();
  return `${name} — ${count}`;
}

// Online first, then everyone else; each row keeps its plan badge.
export function groupMembers(flatMembers, planOrder, plansMap) {
  const buckets = {};
  for (const m of flatMembers) {
    const plan = planKey(m);
    const group = m.onlineStatus === "online" || m.onlineStatus === "away" ? "online" : "offline";
    if (!buckets[group]) buckets[group] = [];
    buckets[group].push({
      id: m.id,
      name: m.displayName || m.username || m.id,
      username: m.username || null,
      joinedAt: m.memberSince ?? m.joinedAt ?? m.createdAt ?? null,
      photoURL: m.photoURL ?? null,
      isAdmin: !!m.isAdmin,
      plan,
      planName: plansMap[plan]?.name ?? plan.charAt(0).toUpperCase() + plan.slice(1),
      planColor: planTierColor(plan, planOrder),
      subscription: m.subscription ?? null,
      status:
        m.onlineStatus === "online" ? "online"
        : m.onlineStatus === "away"  ? "idle"
        : m.onlineStatus === "dnd"   ? "dnd"
        : "offline",
    });
  }
  for (const g of Object.keys(buckets)) {
    buckets[g].sort((a, b) => (planOrder.indexOf(a.plan) - planOrder.indexOf(b.plan)) || String(a.name).localeCompare(String(b.name)));
  }
  return ["online", "offline"].filter((g) => buckets[g]).map((g) => ({
    id: g,
    label: `${g.toUpperCase()} — ${buckets[g].length}`,
    color: g === "online" ? STATUS_COLOR.online : "rgba(255,255,255,0.4)",
    members: buckets[g],
  }));
}

// ─── Status square ────────────────────────────────────────────────────────────

const StatusDot = memo(function StatusDot({ status }) {
  const color = STATUS_COLOR[status] ?? STATUS_COLOR.offline;
  const isOffline = status === "offline";
  return (
    <View
      style={[
        styles.statusDot,
        { backgroundColor: isOffline ? "transparent" : color },
        isOffline && { borderWidth: 1.5, borderColor: color },
      ]}
    />
  );
});

// ─── Member row ───────────────────────────────────────────────────────────────

const MemberRow = memo(function MemberRow({ member, onPress }) {
  const rowRef = useRef(null);
  const isOffline = member.status === "offline";

  const handlePress = useCallback(() => {
    rowRef.current?.measureInWindow((x, y, width, height) => {
      onPress(member, { x, y, width, height });
    });
  }, [member, onPress]);

  return (
    <TouchableOpacity
      ref={rowRef}
      style={styles.memberRow}
      dataSet={{ flRow: "" }}
      activeOpacity={0.85}
      onPress={handlePress}
      accessibilityRole="button"
    >
      <View style={styles.avatarWrap}>
        <View style={[styles.avatar, isOffline && styles.avatarOff]}>
          <Text style={[styles.avatarText, isOffline && { color: FL.ink2 }]}>
            {member.name.charAt(0).toUpperCase()}
          </Text>
        </View>
        <View style={styles.statusBadge}>
          <StatusDot status={member.status} />
        </View>
      </View>

      <View style={styles.memberInfo} dataSet={{ flRowBody: "" }}>
        <Text
          style={[styles.memberName, { color: isOffline ? FL.ink2 : FL.ink, fontWeight: isOffline ? "500" : "700" }]}
          numberOfLines={1}
          dataSet={{ flLit: "" }}
        >
          {member.name}
        </Text>
        {!isOffline && member.status !== "online" && (
          <Text style={styles.memberStatus} numberOfLines={1} dataSet={{ flLit: "" }}>
            {member.status === "idle" ? "Away" : member.status === "dnd" ? "Do Not Disturb" : ""}
          </Text>
        )}
      </View>

      {member.plan !== "free" && <View style={styles.planPip} />}
    </TouchableOpacity>
  );
});

// ─── Group header ─────────────────────────────────────────────────────────────

const GroupHeader = memo(function GroupHeader({ group }) {
  return (
    <View style={styles.groupHeader}>
      <View style={[styles.square, group.id === "online" ? { backgroundColor: FL.pos } : styles.squareOff]} />
      <Text style={[styles.groupLabel, nums]}>{group.label}</Text>
    </View>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function MembersList({ members: membersProp, style }) {
  const [planConfig, setPlanConfig] = useState({ order: FALLBACK_PLAN_ORDER, map: {} });
  const [selection, setSelection] = useState(null);
  const handleSelectMember = useCallback((member, position) => setSelection({ member, position }), []);
  const handleCloseCard    = useCallback(() => setSelection(null), []);

  useEffect(() => {
    getPlans()
      .then((plans) => {
        setPlanConfig({
          order: [...plans.map((p) => p.key), "free"],
          map: Object.fromEntries(plans.map((p) => [p.key, p])),
        });
      })
      .catch(() => {});
  }, []);

  const groupedData = useMemo(
    () => (membersProp ? groupMembers(membersProp, planConfig.order, planConfig.map) : []),
    [membersProp, planConfig],
  );

  const onlineCount = useMemo(
    () => membersProp ? membersProp.filter((m) => m.onlineStatus === "online").length : 0,
    [membersProp],
  );

  const totalCount = useMemo(() => membersProp ? membersProp.length : 0, [membersProp]);

  return (
    <View style={[styles.sidebar, style]}>
      <View style={styles.header}>
        <Text style={styles.headerTitle} accessibilityRole="header">Members</Text>
        <View style={styles.onlineBadge}>
          <View style={styles.onlineDot} />
          <Text style={[styles.onlineCount, nums]}>{onlineCount}</Text>
        </View>
      </View>

      <View style={styles.divider} />

      <ScrollView
        style={styles.list}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 12 }}
      >
        {groupedData.length === 0 ? (
          <Text style={styles.emptyText}>Members appear here once they open Community.</Text>
        ) : (
          groupedData.map((group) => (
            <View key={group.id}>
              <GroupHeader group={group} />
              {group.members.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  onPress={handleSelectMember}
                />
              ))}
            </View>
          ))
        )}
      </ScrollView>

      <View style={styles.footer}>
        <View style={[styles.square, { backgroundColor: FL.pos }]} />
        <Text style={[styles.footerText, nums]}>{onlineCount} online</Text>
        <View style={styles.footerSep} />
        <View style={[styles.square, styles.squareOff]} />
        <Text style={[styles.footerText, nums]}>{totalCount} total</Text>
      </View>

      <MemberCard
        member={selection?.member ?? null}
        position={selection?.position ?? null}
        onClose={handleCloseCard}
      />
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  sidebar: {
    width: SIDEBAR_W,
    height: "100%",
    backgroundColor: "transparent",
    borderLeftWidth: 1,
    borderLeftColor: FL.hair,
    overflow: "hidden",
    flexDirection: "column",
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    height: 60,
  },
  headerTitle: {
    flex: 1,
    color: FL.ink,
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: "800",
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  onlineBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    height: 26,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: FL.hair2,
    backgroundColor: FL.card,
  },
  onlineDot: { width: 7, height: 7, borderRadius: 999, backgroundColor: FL.pos },
  onlineCount: { fontFamily: FONT, fontSize: 12, fontWeight: "800", color: FL.ink },

  divider: { height: 1, backgroundColor: FL.hair },

  list: { flex: 1, paddingTop: 4 },

  emptyText: { ...body(13), color: FL.ink2, textAlign: "center", marginTop: 24, paddingHorizontal: 14 },

  groupHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingTop: 16,
    paddingBottom: 6,
    gap: 8,
  },
  square: { width: 8, height: 8, borderRadius: 999 },
  squareOff: { borderWidth: 1.5, borderColor: FL.muted, backgroundColor: "transparent" },
  groupLabel: { ...label(11), color: FL.muted, fontWeight: "700", letterSpacing: 1.4 },

  memberRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    marginHorizontal: 8,
    marginVertical: 1,
    minHeight: 44,
    borderRadius: 10,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },

  avatarWrap: { position: "relative", marginRight: 10, width: 30, height: 30 },
  avatar: { width: 30, height: 30, borderRadius: 999, alignItems: "center", justifyContent: "center", backgroundColor: FL.stage, borderWidth: 1, borderColor: FL.hair2 },
  avatarOff: { backgroundColor: FL.paper2 },
  avatarText: { color: FL.onStage, fontFamily: FONT, fontSize: 13, fontWeight: "800" },
  statusBadge: { position: "absolute", bottom: -3, right: -3, backgroundColor: FL.paper, padding: 2, borderRadius: 999 },
  statusDot: { width: 8, height: 8, borderRadius: 999 },

  memberInfo: { flex: 1, overflow: "hidden" },
  memberName: { fontFamily: FONT, fontSize: 13.5, lineHeight: 17 },
  memberStatus: { ...label(10), color: FL.ink2, marginTop: 1 },

  planPip: { width: 6, height: 6, borderRadius: 999, marginLeft: 6, backgroundColor: FL.greenInk },

  footer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    height: 44,
    borderTopWidth: 1,
    borderTopColor: FL.hair,
    gap: 6,
  },
  footerText: { ...label(11), color: FL.ink2 },
  footerSep: { width: 1, height: 12, backgroundColor: FL.hair2, marginHorizontal: 4 },
});
